import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';

import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  CreateReportTemplateRequest,
  CreateReportTemplateRequestSheetConfiguration,
  ReportTemplateListResponse,
  ReportTemplateResponse,
  TemplateUploadResponse,
  TemplateUploadResponseSheetUploadInfo,
  UpdateReportTemplateRequest,
  UpdateReportTemplateRequestUpdateSheetConfiguration,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';
import { getNotifyUserMessage } from '~/Utils/errors/notifyUserMessage';

// Type aliases for cleaner exports
export type SheetConfiguration = CreateReportTemplateRequestSheetConfiguration;
export type SheetUploadInfo = TemplateUploadResponseSheetUploadInfo;
export type UpdateSheetConfiguration = UpdateReportTemplateRequestUpdateSheetConfiguration;

// Re-export types for consumers
export type {
  CreateReportTemplateRequest,
  ReportTemplateResponse,
  TemplateUploadResponse,
  UpdateReportTemplateRequest,
};

type UploadTemplateResult =
  | { success: true; data: TemplateUploadResponse }
  | { success: false; userErrorMessage: string | null };

const useReportTemplatesFetch = (shouldFetch = true) => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const baseRoute = `/sysconfig/api/v1/organizations/${organization.id}/reports/templates`;

  // Fetch all templates
  const {
    isLoading,
    isError,
    data: templatesData,
    reloadData: reloadTemplates,
  } = useDataFetcher<ReportTemplateListResponse>(baseRoute, null, { shouldFetch });

  const templates = templatesData?.templates || [];

  const uploadTemplate = async (file: File): Promise<UploadTemplateResult> => {
    try {
      setIsSubmitting(true);
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post<TemplateUploadResponse>(`${baseRoute}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        },
      });

      return { success: true, data };
    } catch (error) {
      // A 'Notify User:' error is an expected user error (e.g. unparseable file) shown
      // inline by the caller; only unexpected errors go through the generic error dialog.
      const userErrorMessage = getNotifyUserMessage(error);
      if (userErrorMessage === null) {
        await reportAxiosError(error);
      }
      return { success: false, userErrorMessage };
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const createTemplate = async (request: CreateReportTemplateRequest): Promise<ReportTemplateResponse | null> => {
    try {
      setIsSubmitting(true);

      const { data } = await axios.post<ReportTemplateResponse>(baseRoute, request);
      await reloadTemplates();

      return data;
    } catch (error) {
      await reportAxiosError(error);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateTemplateStatus = async (templateId: number, isActive: boolean): Promise<boolean> => {
    try {
      setIsSubmitting(true);

      await axios.patch(`${baseRoute}/${templateId}/is_active`, { is_active: isActive });
      await reloadTemplates();

      return true;
    } catch (error) {
      await reportAxiosError(error);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Update an existing report template (name, file, and/or sheets).
   *
   * - If temp_file_id is provided, the file will be replaced
   * - Sheets not included in the request will be deleted
   * - Sheets with id will be updated, sheets without id will be created
   */
  const updateTemplate = async (
    templateId: number,
    request: UpdateReportTemplateRequest
  ): Promise<ReportTemplateResponse | null> => {
    try {
      setIsSubmitting(true);

      const { data } = await axios.patch<ReportTemplateResponse>(`${baseRoute}/${templateId}`, request);
      await reloadTemplates();

      return data;
    } catch (error) {
      await reportAxiosError(error);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    templates,
    isLoading,
    isError,
    reloadTemplates,
    uploadTemplate,
    createTemplate,
    updateTemplate,
    updateTemplateStatus,
    isSubmitting,
    uploadProgress,
  };
};

type ReportTemplatesContextValue = ReturnType<typeof useReportTemplatesFetch>;

const ReportTemplatesContext = createContext<ReportTemplatesContextValue | null>(null);

export const ReportTemplatesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useReportTemplatesFetch(true);
  return <ReportTemplatesContext.Provider value={value}>{children}</ReportTemplatesContext.Provider>;
};

export const useReportTemplates = () => {
  const context = useContext(ReportTemplatesContext);
  const fetched = useReportTemplatesFetch(!context);
  return context ?? fetched;
};
